"use client";

import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { CaretDown, Clock } from "@phosphor-icons/react/dist/ssr";
import {
  getDayLabel,
  DAYS,
  formatRange,
  getVietnamDay,
  getVietnamMinutes,
  hasAnyHours,
  isOpenNow,
  toMinutes,
  type BusinessHours,
  type Day,
} from "@/lib/business-hours";

interface BusinessHoursBlockProps {
  hours: BusinessHours | null;
}

const CLOSING_SOON_MIN = 60;

interface SummaryStatus {
  kind: "open" | "closingSoon" | "closed";
  closesAt?: string;
  nextOpen?: { day: Day; open: string };
}

function statusFromNow(h: BusinessHours, now: Date): SummaryStatus {
  const s = isOpenNow(h, now);
  if (s.open && s.closesAt) {
    const closeMin = toMinutes(s.closesAt);
    const nowMin = getVietnamMinutes(now);
    if (!Number.isNaN(closeMin) && closeMin - nowMin <= CLOSING_SOON_MIN) {
      return { kind: "closingSoon", closesAt: s.closesAt, nextOpen: s.nextOpen };
    }
    return { kind: "open", closesAt: s.closesAt, nextOpen: s.nextOpen };
  }
  return { kind: "closed", nextOpen: s.nextOpen };
}

function rotateFromToday(today: Day): Day[] {
  const idx = DAYS.indexOf(today);
  return [...DAYS.slice(idx), ...DAYS.slice(0, idx)];
}

/**
 * A row inside the navy contact card's "OR REACH OUT DIRECTLY" list.
 * Three render branches:
 *   - hours unset entirely → "Contact for availability" (static)
 *   - by_appointment      → "By appointment only" (static)
 *   - explicit schedule   → live status summary, click to reveal full week
 *
 * The schedule branch is a client component because the open/closed/closing-
 * soon status refreshes every 60s and the day list expand/collapse is
 * interactive. Initial render waits for client mount before showing live
 * status to avoid SSR/CSR drift.
 */
export default function BusinessHoursBlock({ hours }: BusinessHoursBlockProps) {
  const t = useTranslations("profile");
  const locale = useLocale();
  const [now, setNow] = useState<Date | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Hours unset → section absent (consistent with the address section).
  if (!hasAnyHours(hours)) return null;

  const h = hours!;

  if (h.by_appointment) {
    return (
      <li className="flex items-center gap-13 py-13 text-sm text-white/80">
        <Clock size={16} weight="regular" aria-hidden="true" className="shrink-0" />
        <span>{t("byAppointment")}</span>
      </li>
    );
  }

  // Explicit schedule branch
  const today = now ? getVietnamDay(now) : null;
  const status = now ? statusFromNow(h, now) : null;
  const days = rotateFromToday(today ?? "mon");

  const statusColor =
    status?.kind === "open"
      ? "text-green"
      : status?.kind === "closingSoon"
        ? "text-amber"
        : "text-white/80";

  const statusLabel =
    status?.kind === "open"
      ? t("hoursOpen")
      : status?.kind === "closingSoon"
        ? t("hoursClosingSoon")
        : t("hoursClosed");

  return (
    <li className="text-sm text-white/80">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="group flex w-full items-center gap-13 rounded-control py-13 text-left transition-colors hover:text-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
      >
        <Clock size={16} weight="regular" aria-hidden="true" className="shrink-0" />
        <span className="flex flex-wrap items-center gap-x-5">
          {now == null ? (
            // Min-width keeps the row from jumping when the live status
            // (longer than "Hours") replaces the placeholder on mount.
            // `inline-block` is required — `min-width` is ignored on
            // default-inline spans.
            <span className="inline-block min-w-[180px]">{t("hoursLabel")}</span>
          ) : (
            <>
              {/*
                Each colored child needs an explicit `group-hover:text-gold`
                override — `hover:` on the parent button only sets the button's
                own `color`, not descendants that already carry their own
                `text-*` class.
              */}
              <span className={`font-medium transition-colors ${statusColor} group-hover:text-gold`}>
                {statusLabel}
              </span>
              {status?.kind === "open" && status.closesAt && (
                <span className="text-white/80 transition-colors group-hover:text-gold">
                  {t("hoursClosesAt", { time: status.closesAt })}
                </span>
              )}
              {status?.kind === "closingSoon" && status.closesAt && (
                <span className="text-white/80 transition-colors group-hover:text-gold">
                  · {status.closesAt}
                </span>
              )}
              {status?.nextOpen && (
                <span className="text-white/80 transition-colors group-hover:text-gold">
                  {t("hoursOpensAt", { day: getDayLabel(status.nextOpen.day, locale).short, time: status.nextOpen.open })}
                </span>
              )}
            </>
          )}
          <CaretDown
            size={12}
            weight="bold"
            aria-hidden="true"
            className={`ml-3 shrink-0 text-white/70 transition-all duration-150 group-hover:text-gold ${expanded ? "rotate-180" : ""}`}
          />
        </span>
      </button>

      {/*
        CSS Grid `grid-template-rows: 0fr ↔ 1fr` is the only way to animate
        to intrinsic content height without measuring. The inner wrapper
        owns `overflow-hidden` so children (paragraphs, lists) don't bleed
        out during the transition. `aria-hidden` mirrors the visual state
        for screen readers when collapsed.
      */}
      <div
        aria-hidden={!expanded}
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
        <div className="ml-29 mt-5 pb-8">
          <ul role="list" className="space-y-5">
            {days.map((day) => {
            const isToday = day === today;
            const ranges = h[day];
            return (
              <li
                key={day}
                aria-current={isToday ? "date" : undefined}
                className="grid grid-cols-[110px_1fr] items-baseline gap-13"
              >
                <span
                  className={
                    isToday
                      ? "text-sm font-semibold text-white"
                      : "text-sm text-white/80"
                  }
                >
                  {getDayLabel(day, locale).full}
                </span>
                {ranges.length === 0 ? (
                  <span className="text-white/70">{t("hoursClosed")}</span>
                ) : (
                  <span className="space-y-[2px]">
                    {ranges.map((r, i) => (
                      <span
                        key={i}
                        className={`block tabular-nums ${isToday ? "font-semibold text-white" : ""}`}
                      >
                        {formatRange(r)}
                      </span>
                    ))}
                  </span>
                )}
              </li>
            );
          })}
          </ul>
          <p className="mt-13 text-[11px] italic leading-relaxed text-white/70">
            {t("hoursHolidayNote")}
          </p>
        </div>
        </div>
      </div>
    </li>
  );
}
